import { useCallback } from 'react';
import { useRecoilState } from 'recoil';
import {
  accessTokenAtom,
  conversationsAtom,
  frameParamsAtom,
} from 'src/app/state/atoms';
import { IConversationResponseType, IConversations } from 'src/types/serviceResponseTypes';
import { BFF_ENDPOINTS } from 'src/app/constants';
import networkService from 'src/app/lib/network-service';
import captureEvent from 'src/app/lib/gtm-functions';
import { mapFeedbackResponse } from 'src/app/utils/withHistoryMapper';

/**
 * Network hook to start conversation and get initial data
 */
function useStartConversation() {
  const [conversations, setConversations] = useRecoilState(conversationsAtom);
  const [accessToken] = useRecoilState(accessTokenAtom);
  const [frameParams] = useRecoilState(frameParamsAtom);

  const fetchConversation = useCallback(
    async (conversation_id?: string) => {
      setConversations({
        ...conversations,
        loading: true,
      });

      const start = performance.now();
      networkService
        .post<IConversationResponseType>(
          (process.env.REACT_APP_BASE_URL ?? '') + BFF_ENDPOINTS.START_CONVERSATION,
          JSON.stringify({
            ...frameParams,
            conversation_id,
          }),
          {
            'X-Authorization': 'Bearer ' + accessToken,
          }
        )
        .then((response) => {
          const end = performance.now();
          // Telemetry: Output Success
          setTimeout(() => {
            captureEvent({
              event_action: 'Output Success',
              response_duration: `${Math.trunc(end - start)}`,
              conversation_id: response.data?.id,
              locked:
                response.data?.is_open_chat_disabled ||
                response.data?.messages[response?.data.messages?.length - 1]?.locked,
              attempts_left:
                response.data?.messages[response?.data.messages?.length - 1]?.attempts_left,
              chat_action_ids: response.data?.messages?.map((message) => message.id),
              mlm_book_code: frameParams?.book_code ?? 'N/A',
              app_id: 'MLMAITutor',
              exercise_id: frameParams?.exercise_id,
              user_role: frameParams?.['user-role'],
              person_id_type: frameParams?.person_id_type,
              login_session_id: frameParams?.login_session_id,
            });
          }, 700);
          if (response.status) {
            const conversations = {
              ...response.data,
              messages: response.data?.messages.map((conversation) => {
                return {
                  ...conversation,
                  feedback: conversation.feedback
                    ? mapFeedbackResponse(conversation.id, conversation.feedback)
                    : null,
                };
              }),
            };

            setConversations({
              loading: false,
              data: conversations as IConversations,
              error: response.error,
            });
          } else {
            setConversations({
              loading: false,
              data: {} as IConversations,
              error: response.error,
            });
          }
        });
    },
    [setConversations, conversations, frameParams, accessToken]
  );

  return fetchConversation;
}

export default useStartConversation;
